import IconButton from '@components/IconButton/IconButton'
import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Stack, StackProps } from '@mui/material'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

/**
 * A reusable React Hook Form component for managing dynamic field arrays.
 *
 * @template T - The Schema of an individual item in the field array.
 *
 * @component
 * @param {string} name - The name of the field array in the form (e.g., `"items"` or `"addresses"`).
 * @param {T} defaultValue - The object to append when a new item is added to the array.
 * @param {Function} renderField - A render function for each array item.
 *   - Receives an object with:
 *     - `index`: the index of the field array item.
 *     - `remove`: a function to remove the item at the given index.
 *     - `fieldName`: a helper to build dot-notation paths for nested inputs.
 *     - `fieldId`: the unique ID React Hook Form assigns to the item.
 * @param {string} [addButtonLabel='Add'] - The label for the "Add" button.
 * @param {number} [maxItems] - Optional maximum number of items allowed in the array.
 * @param {StackProps} [props] - Additional props to pass to the root MUI `Stack` container.
 */
export type Props<T> = Omit<StackProps, 'defaultValue'> & {
	name: string
	defaultValue: T
	renderField: (params: {
		index: number
		remove: (index: number) => void
		fieldName: (field: keyof T) => string
		fieldId: string
	}) => React.ReactNode
	addButtonLabel?: string
	maxItems?: number
}

export default function RHFFieldArray<T>({
	name,
	defaultValue,
	renderField,
	addButtonLabel = 'Add',
	maxItems = null,
	...props
}: Props<T>) {
	const { control } = useFormContext()
	const { fields, append, remove } = useFieldArray({ control, name })
	const watchedValues = useWatch({ name, control }) as T[] | undefined
	const currentValues = watchedValues ?? (fields as unknown as T[])
	const canAddMore = maxItems ? currentValues.length < maxItems : true

	return (
		<Stack width="100%" alignItems="center" spacing={2} {...props}>
			{fields.map((field, index) => {
				const fieldName = (fieldKey: keyof T) => `${name}.${index}.${String(fieldKey)}`
				return (
					<Stack key={field.id} direction="row" alignItems="center" spacing={2} width="100%">
						<Box sx={{ flex: 1 }}>
							{renderField({
								index,
								remove,
								fieldName,
								fieldId: field.id,
							})}
						</Box>
						<IconButton onClick={() => remove(index)} size="small" icon={faTrash} />
					</Stack>
				)
			})}

			<Button
				onClick={() => append(defaultValue)}
				disabled={!canAddMore}
				color="secondary"
				sx={{ width: 'fit-content' }}
				endIcon={<FontAwesomeIcon icon={faPlus} />}
			>
				{addButtonLabel}
			</Button>
		</Stack>
	)
}
